import { useEffect, useState } from 'react';

/**
 * Custom hook to create a typewriter effect for text
 * @param {string} text - The full text to display
 * @param {number} speed - Speed in milliseconds between each word (default: 50ms)
 * @param {boolean} enabled - Whether the typewriter effect is enabled (default: true)
 * @param {function} onUpdate - Callback fired each time text updates (optional)
 * @returns {string} - The text displayed so far
 */
function useTypewriter(text, speed = 25, enabled = true, onUpdate = null) {
  const [displayedText, setDisplayedText] = useState('');

  useEffect(() => {
    // Ensure text is a string, default to empty string if null/undefined
    const safeText = text || '';

    // If typewriter is disabled, show full text immediately
    if (!enabled) {
      setDisplayedText(safeText);
      if (onUpdate) onUpdate();
      return () => {};
    }

    // Reset when text changes
    setDisplayedText('');

    if (!safeText) {
      return () => {};
    }

    // Split text by words while preserving spaces
    // This regex splits on word boundaries but keeps the spaces
    const words = safeText.match(/\S+\s*/g) || [];
    let currentIndex = 0;

    // Display first word immediately
    if (words.length > 0) {
      setDisplayedText(words[0]);
      currentIndex = 1;
      // Call onUpdate after state update
      if (onUpdate) {
        setTimeout(() => onUpdate(), 0);
      }
    }

    // Continue with remaining words
    const timer = setInterval(() => {
      if (currentIndex < words.length) {
        const nextWord = words[currentIndex];
        if (nextWord !== undefined) {
          setDisplayedText((prev) => prev + nextWord);
        }
        currentIndex += 1;
        // Call onUpdate after state update
        if (onUpdate) {
          setTimeout(() => onUpdate(), 0);
        }
      } else {
        clearInterval(timer);
      }
    }, speed);

    return () => {
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, speed, enabled]);

  return displayedText;
}

export default useTypewriter;
